import { formatAppDate, formatAppDateTime } from "../../app/utils/appTimeFormat";

export function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppDate(d);
}

export function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppDateTime(d);
}

/** Compact ledger subline (country + season + fixtures + window). */
export function leagueLedgerSubline(league) {
  const parts = [];
  if (league?.country?.name) parts.push(league.country.name);
  if (league?.currentSeasonYear != null) parts.push(`S${league.currentSeasonYear}`);
  const fixtures = Number(league?.fixtureCount) || 0;
  parts.push(`${fixtures} fixtures`);
  const start = formatDate(league?.currentSeasonStartDate);
  const end = formatDate(league?.currentSeasonEndDate);
  if (start !== "—" || end !== "—") {
    parts.push(`${start} → ${end}`);
  }
  if (league?.seasonPhase === "no_upcoming") parts.push("0 upcoming");
  return parts.length ? parts.join(" · ") : "—";
}

export function formatRelative(value) {
  if (!value) return "Never";
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return formatDate(value);
}

export function fixtureStatusTone(short) {
  if (!short) return "muted";
  if (["1H", "2H", "HT", "ET", "BT", "P", "LIVE", "INT"].includes(short)) return "live";
  if (["FT", "AET", "PEN"].includes(short)) return "muted";
  if (["CANC", "ABD", "PST"].includes(short)) return "warn";
  return "default";
}

export function resolveSeasonPhase(league, upcomingFixtureCount = null) {
  const now = Date.now();
  const start = league?.currentSeasonStartDate
    ? new Date(league.currentSeasonStartDate).getTime()
    : null;
  const end = league?.currentSeasonEndDate
    ? new Date(league.currentSeasonEndDate).getTime()
    : null;

  if (start != null && !Number.isNaN(start) && now < start) return "upcoming";
  if (end != null && !Number.isNaN(end) && now > end) return "ended";

  let calendarPhase = "unknown";
  if (
    start != null &&
    !Number.isNaN(start) &&
    end != null &&
    !Number.isNaN(end) &&
    now >= start &&
    now <= end
  ) {
    calendarPhase = "in_season";
  } else if (end != null && !Number.isNaN(end) && now <= end) {
    calendarPhase = "in_season";
  } else if (start != null && !Number.isNaN(start) && now >= start) {
    calendarPhase = "in_season";
  }

  if (calendarPhase !== "in_season") return calendarPhase;
  if (upcomingFixtureCount == null) return "in_season";
  return upcomingFixtureCount > 0 ? "in_season" : "no_upcoming";
}

export function groupLeaguesBySeasonPhase(leagues) {
  const order = ["in_season", "no_upcoming", "upcoming", "ended", "unknown"];
  const buckets = Object.fromEntries(order.map((id) => [id, []]));

  for (const league of leagues || []) {
    const phase =
      league.seasonPhase ||
      resolveSeasonPhase(league, league.upcomingFixtureCount ?? null);
    (buckets[phase] || buckets.unknown).push(league);
  }

  return order
    .map((id) => ({ id, leagues: buckets[id] || [] }))
    .filter((group) => group.leagues.length > 0);
}
